import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { FeatureFlag } from '@/components/feature-flag/FeatureFlag';
import { FeatureDisabledPlaceholder } from '@/components/feature-flag/FeatureDisabledPlaceholder';
import { FeatureFlag as Flag, type EFeatureFlag } from '@/lib/feature-flags/keys';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { PageLoader } from '@/components/ui/page-loader';
const LoginForm = lazy(() =>
  import('@/features/auth/components/LoginForm').then((m) => ({ default: m.LoginForm })),
);
const SetPasswordForm = lazy(() =>
  import('@/features/auth/components/SetPasswordForm').then((m) => ({
    default: m.SetPasswordForm,
  })),
);
const ForgotPasswordForm = lazy(() =>
  import('@/features/auth/components/ForgotPasswordForm').then((m) => ({
    default: m.ForgotPasswordForm,
  })),
);
const ResetPasswordForm = lazy(() =>
  import('@/features/auth/components/ResetPasswordForm').then((m) => ({
    default: m.ResetPasswordForm,
  })),
);

// Dashboard
const KpiBuilderPage = lazy(() =>
  import('@/features/dashboard/components/KpiBuilderPage').then((m) => ({
    default: m.KpiBuilderPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/components/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  })),
);

// Super Admin
const TenantsListPage = lazy(() =>
  import('@/features/super-admin/components/TenantsListPage').then((m) => ({
    default: m.TenantsListPage,
  })),
);
const SATenantDetailPage = lazy(() =>
  import('@/features/super-admin/components/TenantDetailPage').then((m) => ({
    default: m.TenantDetailPage,
  })),
);
const ProvisionTenantPage = lazy(() =>
  import('@/features/super-admin/components/ProvisionTenantPage').then((m) => ({
    default: m.ProvisionTenantPage,
  })),
);
const OnboardingQueuePage = lazy(() =>
  import('@/features/super-admin/components/OnboardingQueuePage').then((m) => ({
    default: m.OnboardingQueuePage,
  })),
);
const ImpersonationPage = lazy(() =>
  import('@/features/super-admin/components/ImpersonationPage').then((m) => ({
    default: m.ImpersonationPage,
  })),
);
const SystemHealthPage = lazy(() =>
  import('@/features/super-admin/components/SystemHealthPage').then((m) => ({
    default: m.SystemHealthPage,
  })),
);
const ServiceHealthDetailPage = lazy(() =>
  import('@/features/super-admin/components/system-health/ServiceHealthDetailPage').then(
    (m) => ({ default: m.ServiceHealthDetailPage }),
  ),
);
const JobQueuesPage = lazy(() =>
  import('@/features/super-admin/components/JobQueuesPage').then((m) => ({
    default: m.JobQueuesPage,
  })),
);
const ErrorLogsPage = lazy(() =>
  import('@/features/super-admin/components/ErrorLogsPage').then((m) => ({
    default: m.ErrorLogsPage,
  })),
);
const SecurityFeedPage = lazy(() =>
  import('@/features/super-admin/components/SecurityFeedPage').then((m) => ({
    default: m.SecurityFeedPage,
  })),
);
const FailedLoginsPage = lazy(() =>
  import('@/features/super-admin/components/FailedLoginsPage').then((m) => ({
    default: m.FailedLoginsPage,
  })),
);
const PlatformAuditPage = lazy(() =>
  import('@/features/super-admin/components/PlatformAuditPage').then((m) => ({
    default: m.PlatformAuditPage,
  })),
);
const UsagePlatformAnalyticsPage = lazy(() =>
  import('@/features/super-admin/components/UsagePlatformAnalyticsPage').then((m) => ({
    default: m.UsagePlatformAnalyticsPage,
  })),
);
const TenantUsageAnalyticsPage = lazy(() =>
  import('@/features/super-admin/components/TenantUsageAnalyticsPage').then(
    (m) => ({ default: m.TenantUsageAnalyticsPage }),
  ),
);
const PlatformSettingsPage = lazy(() =>
  import('@/features/super-admin/components/PlatformSettingsPage').then((m) => ({
    default: m.PlatformSettingsPage,
  })),
);
const SecurityPoliciesPage = lazy(() =>
  import('@/features/super-admin/components/SecurityPoliciesPage').then((m) => ({
    default: m.SecurityPoliciesPage,
  })),
);
const KYBConfigPage = lazy(() =>
  import('@/features/super-admin/components/KYBConfigPage').then((m) => ({
    default: m.KYBConfigPage,
  })),
);
const KYBReviewsPage = lazy(() =>
  import('@/features/super-admin/components/KYBReviewsPage').then((m) => ({
    default: m.KYBReviewsPage,
  })),
);
const KYBReviewDetailPage = lazy(() =>
  import('@/features/super-admin/components/KYBReviewDetailPage').then((m) => ({
    default: m.KYBReviewDetailPage,
  })),
);

// Compliance
const LicenseRegisterPage = lazy(() =>
  import('@/features/compliance/components/LicenseRegisterPage').then((m) => ({
    default: m.LicenseRegisterPage,
  })),
);
const LicenseRenewalsPage = lazy(() =>
  import('@/features/compliance/components/LicenseRenewalsPage').then((m) => ({
    default: m.LicenseRenewalsPage,
  })),
);
const LicenseDetailPage = lazy(() =>
  import('@/features/compliance/components/LicenseDetailPage').then((m) => ({
    default: m.LicenseDetailPage,
  })),
);
const AddLicensePage = lazy(() =>
  import('@/features/compliance/components/AddLicensePage').then((m) => ({
    default: m.AddLicensePage,
  })),
);
const EditLicensePage = lazy(() =>
  import('@/features/compliance/components/EditLicensePage').then((m) => ({
    default: m.EditLicensePage,
  })),
);
const ComplianceRequirementsPage = lazy(() =>
  import('@/features/compliance/components/ComplianceRequirementsPage').then((m) => ({
    default: m.ComplianceRequirementsPage,
  })),
);
const RequirementDetailPage = lazy(() =>
  import('@/features/compliance/components/RequirementDetailPage').then((m) => ({
    default: m.RequirementDetailPage,
  })),
);
const EditRequirementPage = lazy(() =>
  import('@/features/compliance/components/EditRequirementPage').then((m) => ({
    default: m.EditRequirementPage,
  })),
);
const ComplianceCalendarPage = lazy(() =>
  import('@/features/compliance/components/ComplianceCalendarPage').then((m) => ({
    default: m.ComplianceCalendarPage,
  })),
);
const CommitmentTrackerPage = lazy(() =>
  import('@/features/compliance/components/CommitmentTrackerPage').then((m) => ({
    default: m.CommitmentTrackerPage,
  })),
);
const CommitmentDetailPage = lazy(() =>
  import('@/features/compliance/components/CommitmentDetailPage').then((m) => ({
    default: m.CommitmentDetailPage,
  })),
);
const AddCommitmentPage = lazy(() =>
  import('@/features/compliance/components/AddCommitmentPage').then((m) => ({
    default: m.AddCommitmentPage,
  })),
);
const PermitAlertsPage = lazy(() =>
  import('@/features/compliance/components/PermitAlertsPage').then((m) => ({
    default: m.PermitAlertsPage,
  })),
);
const DocumentRepositoryPage = lazy(() =>
  import('@/features/compliance/components/DocumentRepositoryPage').then((m) => ({
    default: m.DocumentRepositoryPage,
  })),
);
// const ComplianceReportingPage = lazy(() => import('@/features/compliance/components/ComplianceReportingPage').then((m) => ({ default: m.ComplianceReportingPage }))); // Moved to Dashboard
const ComplianceReportsHubPage = lazy(() =>
  import('@/features/compliance/components/ComplianceReportsHubPage').then((m) => ({
    default: m.ComplianceReportsHubPage,
  })),
);
const InspectionFeedbackPage = lazy(() =>
  import('@/features/compliance/components/InspectionFeedbackPage').then((m) => ({
    default: m.InspectionFeedbackPage,
  })),
);

// Developer platform — API keys, webhooks, IP allowlist
const ApiKeysPage = lazy(() =>
  import('@/features/developer/components/ApiKeysPage').then((m) => ({
    default: m.ApiKeysPage,
  })),
);
const WebhooksPage = lazy(() =>
  import('@/features/developer/components/WebhooksPage').then((m) => ({
    default: m.WebhooksPage,
  })),
);
const DeliveryLogPage = lazy(() =>
  import('@/features/developer/components/DeliveryLogPage').then((m) => ({
    default: m.DeliveryLogPage,
  })),
);
const IpAllowlistPage = lazy(() =>
  import('@/features/developer/components/IpAllowlistPage').then((m) => ({
    default: m.IpAllowlistPage,
  })),
);

// Geology & Resource Management — Appendix B.4 / §6.2
const DrillHoleRegisterPage = lazy(() =>
  import('@/features/geology/components/DrillHoleRegisterPage').then((m) => ({
    default: m.DrillHoleRegisterPage,
  })),
);
const DrillHoleDetailPage = lazy(() =>
  import('@/features/geology/components/DrillHoleDetailPage').then((m) => ({
    default: m.DrillHoleDetailPage,
  })),
);
const ResourceBlocksPage = lazy(() =>
  import('@/features/geology/components/ResourceBlocksPage').then((m) => ({
    default: m.ResourceBlocksPage,
  })),
);
const ResourceStatementsPage = lazy(() =>
  import('@/features/geology/components/ResourceStatementsPage').then((m) => ({
    default: m.ResourceStatementsPage,
  })),
);
const ResourceStatementDetailPage = lazy(() =>
  import('@/features/geology/components/ResourceStatementDetailPage').then((m) => ({
    default: m.ResourceStatementDetailPage,
  })),
);
const MapRecordsPage = lazy(() =>
  import('@/features/geology/components/MapRecordsPage').then((m) => ({
    default: m.MapRecordsPage,
  })),
);
const GradeSamplesPage = lazy(() =>
  import('@/features/operations/components/GradeSamplesPage').then((m) => ({
    default: m.GradeSamplesPage,
  })),
);

// Laboratory (LIMS) — Appendix B.4 / §6.5
const LabSampleRegisterPage = lazy(() =>
  import('@/features/lims/components/LabSampleRegisterPage').then((m) => ({
    default: m.LabSampleRegisterPage,
  })),
);
const LabSampleDetailPage = lazy(() =>
  import('@/features/lims/components/LabSampleDetailPage').then((m) => ({
    default: m.LabSampleDetailPage,
  })),
);
const BatchQaqcPage = lazy(() =>
  import('@/features/lims/components/BatchQaqcPage').then((m) => ({
    default: m.BatchQaqcPage,
  })),
);
const GradeCertificatesPage = lazy(() =>
  import('@/features/lims/components/GradeCertificatesPage').then((m) => ({
    default: m.GradeCertificatesPage,
  })),
);
const ImportResultsPage = lazy(() =>
  import('@/features/lims/components/ImportResultsPage').then((m) => ({
    default: m.ImportResultsPage,
  })),
);

// Ore Processing — Appendix B.4 / §6.1
const PlantsPage = lazy(() =>
  import('@/features/ore-processing/components/PlantsPage').then((m) => ({
    default: m.PlantsPage,
  })),
);
const PlantDetailPage = lazy(() =>
  import('@/features/ore-processing/components/PlantDetailPage').then((m) => ({
    default: m.PlantDetailPage,
  })),
);
const ProcessingBatchesPage = lazy(() =>
  import('@/features/ore-processing/components/ProcessingBatchesPage').then((m) => ({
    default: m.ProcessingBatchesPage,
  })),
);
const ProcessingBatchDetailPage = lazy(() =>
  import('@/features/ore-processing/components/ProcessingBatchDetailPage').then((m) => ({
    default: m.ProcessingBatchDetailPage,
  })),
);

// Surveying & Mapping — Appendix B.4 / §6.6
const SurveyRegisterPage = lazy(() =>
  import('@/features/surveying/components/SurveyRegisterPage').then((m) => ({
    default: m.SurveyRegisterPage,
  })),
);
const SurveyDetailPage = lazy(() =>
  import('@/features/surveying/components/SurveyDetailPage').then((m) => ({
    default: m.SurveyDetailPage,
  })),
);
const ReconciliationQueuePage = lazy(() =>
  import('@/features/surveying/components/ReconciliationQueuePage').then((m) => ({
    default: m.ReconciliationQueuePage,
  })),
);
const CoordinateSystemPage = lazy(() =>
  import('@/features/surveying/components/CoordinateSystemPage').then((m) => ({
    default: m.CoordinateSystemPage,
  })),
);

// Procurement & Vendors — Appendix B.4 / §6.4
const VendorsPage = lazy(() =>
  import('@/features/procurement/components/VendorsPage').then((m) => ({
    default: m.VendorsPage,
  })),
);
const VendorDetailPage = lazy(() =>
  import('@/features/procurement/components/VendorDetailPage').then((m) => ({
    default: m.VendorDetailPage,
  })),
);
const PurchaseOrdersPage = lazy(() =>
  import('@/features/procurement/components/PurchaseOrdersPage').then((m) => ({
    default: m.PurchaseOrdersPage,
  })),
);
const PurchaseOrderDetailPage = lazy(() =>
  import('@/features/procurement/components/PurchaseOrderDetailPage').then((m) => ({
    default: m.PurchaseOrderDetailPage,
  })),
);
const ApprovalBandsPage = lazy(() =>
  import('@/features/procurement/components/ApprovalBandsPage').then((m) => ({
    default: m.ApprovalBandsPage,
  })),
);
const SpendAnalyticsPage = lazy(() =>
  import('@/features/procurement/components/SpendAnalyticsPage').then((m) => ({
    default: m.SpendAnalyticsPage,
  })),
);

// Supply Chain & Logistics — Appendix B.4 / §6.7
const CarriersPage = lazy(() =>
  import('@/features/logistics/components/CarriersPage').then((m) => ({
    default: m.CarriersPage,
  })),
);
const CarrierDetailPage = lazy(() =>
  import('@/features/logistics/components/CarrierDetailPage').then((m) => ({
    default: m.CarrierDetailPage,
  })),
);
const DispatchBoardPage = lazy(() =>
  import('@/features/logistics/components/DispatchBoardPage').then((m) => ({
    default: m.DispatchBoardPage,
  })),
);
const DispatchDetailPage = lazy(() =>
  import('@/features/logistics/components/DispatchDetailPage').then((m) => ({
    default: m.DispatchDetailPage,
  })),
);
const DiscrepancyQueuePage = lazy(() =>
  import('@/features/logistics/components/DiscrepancyQueuePage').then((m) => ({
    default: m.DiscrepancyQueuePage,
  })),
);

// Operations
const ProductionOverviewPage = lazy(() =>
  import('@/features/operations/components/ProductionOverviewPage').then((m) => ({
    default: m.ProductionOverviewPage,
  })),
);
const SiteHierarchyPage = lazy(() =>
  import('@/features/operations/components/SiteHierarchyPage').then((m) => ({
    default: m.SiteHierarchyPage,
  })),
);
const ZoneDetailPage = lazy(() =>
  import('@/features/operations/components/ZoneDetailPage').then((m) => ({
    default: m.ZoneDetailPage,
  })),
);

// Critical Alerts
const CriticalAlertsPage = lazy(() =>
  import('@/features/alerts/components/CriticalAlertsPage').then((m) => ({
    default: m.CriticalAlertsPage,
  })),
);
const MineSetupWizardPage = lazy(() =>
  import('@/features/operations/components/MineSetupWizard').then((m) => ({
    default: m.MineSetupWizardPage,
  })),
);
const InspectorOnboardingPage = lazy(() =>
  import('@/features/auth/components/InspectorOnboardingPage').then((m) => ({
    default: m.InspectorOnboardingPage,
  })),
);
const WeeklyMineReportPage = lazy(() =>
  import('@/features/operations/components/WeeklyMineReportPage').then((m) => ({
    default: m.WeeklyMineReportPage,
  })),
);
const DailyProductionLogPage = lazy(() =>
  import('@/features/operations/components/DailyProductionLogPage').then((m) => ({
    default: m.DailyProductionLogPage,
  })),
);
const ShiftLogsPage = lazy(() =>
  import('@/features/operations/components/ShiftLogsPage').then((m) => ({
    default: m.ShiftLogsPage,
  })),
);
const DevelopmentTrackerPage = lazy(() =>
  import('@/features/operations/components/DevelopmentTrackerPage').then((m) => ({
    default: m.DevelopmentTrackerPage,
  })),
);
const WorkingDayCalendarPage = lazy(() =>
  import('@/features/operations/components/WorkingDayCalendarPage').then((m) => ({
    default: m.WorkingDayCalendarPage,
  })),
);
const BagTrackingPage = lazy(() =>
  import('@/features/operations/components/BagTrackingPage').then((m) => ({
    default: m.BagTrackingPage,
  })),
);
const MEUDetailPage = lazy(() =>
  import('@/features/operations/components/MEUDetailPage').then((m) => ({
    default: m.MEUDetailPage,
  })),
);
const BlastingLogPage = lazy(() =>
  import('@/features/operations/components/BlastingLogPage').then((m) => ({
    default: m.BlastingLogPage,
  })),
);
const BlasterCertificateRegistryPage = lazy(() =>
  import('@/features/operations/components/BlasterCertificateRegistryPage').then((m) => ({
    default: m.BlasterCertificateRegistryPage,
  })),
);
const TailingsTrackingPage = lazy(() =>
  import('@/features/operations/components/TailingsTrackingPage').then((m) => ({
    default: m.TailingsTrackingPage,
  })),
);
const HashChainVerificationPage = lazy(() =>
  import('@/features/operations/components/HashChainVerificationPage').then((m) => ({
    default: m.HashChainVerificationPage,
  })),
);
const LotManagementPage = lazy(() =>
  import('@/features/operations/components/LotManagementPage').then((m) => ({
    default: m.LotManagementPage,
  })),
);
const LotDetailPage = lazy(() =>
  import('@/features/operations/components/LotDetailPage').then((m) => ({
    default: m.LotDetailPage,
  })),
);
const TraceabilityCertificatePage = lazy(() =>
  import('@/features/operations/components/TraceabilityCertificatePage').then((m) => ({
    default: m.TraceabilityCertificatePage,
  })),
);

// Workforce
const WorkerRegistryPage = lazy(() =>
  import('@/features/workforce/components/WorkerRegistryPage').then((m) => ({
    default: m.WorkerRegistryPage,
  })),
);
const WorkerApprovalsPage = lazy(() =>
  import('@/features/workforce/components/WorkerApprovalsPage').then((m) => ({
    default: m.WorkerApprovalsPage,
  })),
);
const WorkerRegistrationForm = lazy(() =>
  import('@/features/workforce/components/WorkerRegistrationForm').then((m) => ({
    default: m.WorkerRegistrationForm,
  })),
);
const WorkerProfilePage = lazy(() =>
  import('@/features/workforce/components/WorkerProfilePage').then((m) => ({
    default: m.WorkerProfilePage,
  })),
);
const AttendanceListPage = lazy(() =>
  import('@/features/workforce/components/AttendanceListPage').then((m) => ({
    default: m.AttendanceListPage,
  })),
);
const AttendanceDetailPage = lazy(() =>
  import('@/features/workforce/components/AttendanceDetailPage').then((m) => ({
    default: m.AttendanceDetailPage,
  })),
);
const CompensationConfigPage = lazy(() =>
  import('@/features/workforce/components/CompensationConfigPage').then((m) => ({
    default: m.CompensationConfigPage,
  })),
);
const PaymentBatchesPage = lazy(() =>
  import('@/features/workforce/components/PaymentBatchesPage').then((m) => ({
    default: m.PaymentBatchesPage,
  })),
);
const SubcontractorsPage = lazy(() =>
  import('@/features/workforce/components/SubcontractorsPage').then((m) => ({
    default: m.SubcontractorsPage,
  })),
);

// Safety
const SafetyDashboardPage = lazy(() =>
  import('@/features/safety/components/SafetyDashboardPage').then((m) => ({
    default: m.SafetyDashboardPage,
  })),
);
const IncidentRegisterPage = lazy(() =>
  import('@/features/safety/components/IncidentRegisterPage').then((m) => ({
    default: m.IncidentRegisterPage,
  })),
);
const ReportIncidentPage = lazy(() =>
  import('@/features/safety/components/ReportIncidentPage').then((m) => ({
    default: m.ReportIncidentPage,
  })),
);
const VisitorInductionFormPage = lazy(() =>
  import('@/features/visitors/components/VisitorInductionFormPage').then((m) => ({
    default: m.VisitorInductionFormPage,
  })),
);
const VisitorRegisterPage = lazy(() =>
  import('@/features/visitors/components/VisitorRegisterPage').then((m) => ({
    default: m.VisitorRegisterPage,
  })),
);
const VisitorApprovalsPage = lazy(() =>
  import('@/features/visitors/components/VisitorApprovalsPage').then((m) => ({
    default: m.VisitorApprovalsPage,
  })),
);
const VisitorDetailPage = lazy(() =>
  import('@/features/visitors/components/VisitorDetailPage').then((m) => ({
    default: m.VisitorDetailPage,
  })),
);
const IncidentDetailPage = lazy(() =>
  import('@/features/safety/components/IncidentDetailPage').then((m) => ({
    default: m.IncidentDetailPage,
  })),
);
const IncidentTypeConfigPage = lazy(() =>
  import('@/features/safety/components/IncidentTypeConfigPage').then((m) => ({
    default: m.IncidentTypeConfigPage,
  })),
);
const InspectionTypeConfigPage = lazy(() =>
  import('@/features/safety/components/InspectionTypeConfigPage').then((m) => ({
    default: m.InspectionTypeConfigPage,
  })),
);
const InspectionChecklistTemplatePage = lazy(() =>
  import('@/features/safety/components/InspectionChecklistTemplatePage').then((m) => ({
    default: m.InspectionChecklistTemplatePage,
  })),
);
const RiskClassificationConfigPage = lazy(() =>
  import('@/features/safety/components/RiskClassificationConfigPage').then((m) => ({
    default: m.RiskClassificationConfigPage,
  })),
);
const InspectionSchedulePage = lazy(() =>
  import('@/features/safety/components/InspectionSchedulePage').then((m) => ({
    default: m.InspectionSchedulePage,
  })),
);
const InspectionFormPage = lazy(() =>
  import('@/features/safety/components/InspectionFormPage').then((m) => ({
    default: m.InspectionFormPage,
  })),
);
const InspectionDetailPage = lazy(() =>
  import('@/features/safety/components/InspectionDetailPage').then((m) => ({
    default: m.InspectionDetailPage,
  })),
);
const HazardRegisterPage = lazy(() =>
  import('@/features/safety/components/HazardRegisterPage').then((m) => ({
    default: m.HazardRegisterPage,
  })),
);
const CorrectiveActionsPage = lazy(() =>
  import('@/features/safety/components/CorrectiveActionsPage').then((m) => ({
    default: m.CorrectiveActionsPage,
  })),
);
const CorrectiveActionDetailPage = lazy(() =>
  import('@/features/safety/components/CorrectiveActionDetailPage').then((m) => ({
    default: m.CorrectiveActionDetailPage,
  })),
);
const EnvironmentalMonitoringPage = lazy(() =>
  import('@/features/safety/components/EnvironmentalMonitoringPage').then((m) => ({
    default: m.EnvironmentalMonitoringPage,
  })),
);
const EnvironmentalIncidentsPage = lazy(() =>
  import('@/features/safety/components/EnvironmentalIncidentsPage').then((m) => ({
    default: m.EnvironmentalIncidentsPage,
  })),
);
const EnvironmentalIncidentDetailPage = lazy(() =>
  import('@/features/safety/components/EnvironmentalIncidentDetailPage').then((m) => ({
    default: m.EnvironmentalIncidentDetailPage,
  })),
);
const EnvironmentalCompliancePage = lazy(() =>
  import('@/features/safety/components/EnvironmentalCompliancePage').then((m) => ({
    default: m.EnvironmentalCompliancePage,
  })),
);

// Inventory
const InventoryPage = lazy(() =>
  import('@/features/inventory/components/InventoryPage').then((m) => ({
    default: m.InventoryPage,
  })),
);
const MaterialDetailPage = lazy(() =>
  import('@/features/inventory/components/MaterialDetailPage').then((m) => ({
    default: m.MaterialDetailPage,
  })),
);
const StockOnHandPage = lazy(() =>
  import('@/features/inventory/components/StockOnHandPage').then((m) => ({
    default: m.StockOnHandPage,
  })),
);
const StockMovementsPage = lazy(() =>
  import('@/features/inventory/components/StockMovementsPage').then((m) => ({
    default: m.StockMovementsPage,
  })),
);
const StockCountsPage = lazy(() =>
  import('@/features/inventory/components/StockCountsPage').then((m) => ({
    default: m.StockCountsPage,
  })),
);
const StockCountDetailPage = lazy(() =>
  import('@/features/inventory/components/StockCountDetailPage').then((m) => ({
    default: m.StockCountDetailPage,
  })),
);
const OreStockpilePage = lazy(() =>
  import('@/features/inventory/components/OreStockpilePage').then((m) => ({
    default: m.OreStockpilePage,
  })),
);
const MaterialRequisitionsPage = lazy(() =>
  import('@/features/inventory/components/MaterialRequisitionsPage').then((m) => ({
    default: m.MaterialRequisitionsPage,
  })),
);
const MaterialReconciliationPage = lazy(() =>
  import('@/features/inventory/components/MaterialReconciliationPage').then((m) => ({
    default: m.MaterialReconciliationPage,
  })),
);
const CategoriesPage = lazy(() =>
  import('@/features/inventory/components/CategoriesPage').then((m) => ({
    default: m.CategoriesPage,
  })),
);
const EquipmentAvailabilityPage = lazy(() =>
  import('@/features/inventory/components/EquipmentAvailabilityPage').then((m) => ({
    default: m.EquipmentAvailabilityPage,
  })),
);
const SafetyEquipmentPage = lazy(() =>
  import('@/features/inventory/components/SafetyEquipmentPage').then((m) => ({
    default: m.SafetyEquipmentPage,
  })),
);
const EquipmentShiftLogPage = lazy(() =>
  import('@/features/operations/components/EquipmentShiftLogPage').then((m) => ({
    default: m.EquipmentShiftLogPage,
  })),
);
const MaterialAvailabilityPage = lazy(() =>
  import('@/features/operations/components/MaterialAvailabilityPage').then((m) => ({
    default: m.MaterialAvailabilityPage,
  })),
);
const ProductionLogsPage = lazy(() =>
  import('@/features/operations/components/ProductionLogsPage').then((m) => ({
    default: m.ProductionLogsPage,
  })),
);
const WeeklyProductionSummaryPage = lazy(() =>
  import('@/features/operations/components/WeeklyProductionSummaryPage').then((m) => ({
    default: m.WeeklyProductionSummaryPage,
  })),
);
const LabourRecordDetailPage = lazy(() =>
  import('@/features/workforce/components/LabourRecordDetailPage').then((m) => ({
    default: m.LabourRecordDetailPage,
  })),
);
const ProductionTargetConfigPage = lazy(() =>
  import('@/features/operations/components/ProductionTargetConfigPage').then((m) => ({
    default: m.ProductionTargetConfigPage,
  })),
);
const TeamLabourLogPage = lazy(() =>
  import('@/features/workforce/components/TeamLabourLogPage').then((m) => ({
    default: m.TeamLabourLogPage,
  })),
);
const IntegrityReportPage = lazy(() =>
  import('@/features/operations/components/IntegrityReportPage').then((m) => ({
    default: m.IntegrityReportPage,
  })),
);

// Finance
const PaymentBatchDetailPage = lazy(() =>
  import('@/features/finance/components/PaymentBatchDetailPage').then((m) => ({
    default: m.PaymentBatchDetailPage,
  })),
);
const ReconciliationDashboardPage = lazy(() =>
  import('@/features/finance/components/ReconciliationDashboardPage').then((m) => ({
    default: m.ReconciliationDashboardPage,
  })),
);
// Payment Configuration is switched off for every user; remove the comments to bring it back.
// const PaymentConfigPage = lazy(() =>
//   import('@/features/finance/components/PaymentConfigPage').then((m) => ({
//     default: m.PaymentConfigPage,
//   })),
// );
const PaymentHistoryPage = lazy(() =>
  import('@/features/finance/components/PaymentHistoryPage').then((m) => ({
    default: m.PaymentHistoryPage,
  })),
);
const FinancialRequisitionRegisterPage = lazy(() =>
  import('@/features/finance/components/FinancialRequisitionRegisterPage').then((m) => ({
    default: m.FinancialRequisitionRegisterPage,
  })),
);
const FinancialRequisitionFormPage = lazy(() =>
  import('@/features/finance/components/FinancialRequisitionFormPage').then((m) => ({
    default: m.FinancialRequisitionFormPage,
  })),
);
const FinancialRequisitionDetailPage = lazy(() =>
  import('@/features/finance/components/FinancialRequisitionDetailPage').then((m) => ({
    default: m.FinancialRequisitionDetailPage,
  })),
);
const PaymentRunsPage = lazy(() =>
  import('@/features/finance/components/PaymentRunsPage').then((m) => ({
    default: m.PaymentRunsPage,
  })),
);
const PaymentRunDetailPage = lazy(() =>
  import('@/features/finance/components/PaymentRunDetailPage').then((m) => ({
    default: m.PaymentRunDetailPage,
  })),
);

// Reports
const ReportsPage = lazy(() =>
  import('@/features/reports/components/ReportsPage').then((m) => ({
    default: m.ReportsPage,
  })),
);

// Audit
const AuditLogPage = lazy(() =>
  import('@/features/audit/components/AuditLogPage').then((m) => ({
    default: m.AuditLogPage,
  })),
);

// Config / Users
const UserManagementPage = lazy(() =>
  import('@/features/tenant-config/components/UserManagementPage').then((m) => ({
    default: m.UserManagementPage,
  })),
);
const UserDetailPage = lazy(() =>
  import('@/features/tenant-config/components/UserDetailPage').then((m) => ({
    default: m.UserDetailPage,
  })),
);
const RolesPermissionsPage = lazy(() =>
  import('@/features/tenant-config/components/RolesPermissionsPage').then((m) => ({
    default: m.RolesPermissionsPage,
  })),
);
const ShiftsPage = lazy(() =>
  import('@/features/operations/components/ShiftsPage').then((m) => ({
    default: m.ShiftsPage,
  })),
);
const ShiftDetailPage = lazy(() =>
  import('@/features/operations/components/ShiftDetailPage').then((m) => ({
    default: m.ShiftDetailPage,
  })),
);
const DepartmentsPage = lazy(() =>
  import('@/features/tenant-config/components/DepartmentsPage').then((m) => ({
    default: m.DepartmentsPage,
  })),
);
const WorkingPlacesRegisterPage = lazy(() =>
  import('@/features/operations/components/WorkingPlacesRegisterPage').then((m) => ({
    default: m.WorkingPlacesRegisterPage,
  })),
);
const WorkingPlaceTypesPage = lazy(() =>
  import('@/features/operations/components/WorkingPlaceTypesPage').then((m) => ({
    default: m.WorkingPlaceTypesPage,
  })),
);
const PlanningMetricsPage = lazy(() =>
  import('@/features/planning/components/PlanningMetricsPage').then((m) => ({
    default: m.PlanningMetricsPage,
  })),
);
const PlanningCodeListsPage = lazy(() =>
  import('@/features/planning/components/PlanningCodeListsPage').then((m) => ({
    default: m.PlanningCodeListsPage,
  })),
);
const FinancialYearPage = lazy(() =>
  import('@/features/planning/components/FinancialYearPage').then((m) => ({
    default: m.FinancialYearPage,
  })),
);
const PlanVersionsPage = lazy(() =>
  import('@/features/planning/components/PlanVersionsPage').then((m) => ({
    default: m.PlanVersionsPage,
  })),
);
const SiteOperatingCalendarPage = lazy(() =>
  import('@/features/planning/components/SiteOperatingCalendarPage').then((m) => ({
    default: m.SiteOperatingCalendarPage,
  })),
);
const DailyTargetsPage = lazy(() =>
  import('@/features/planning/components/DailyTargetsPage').then((m) => ({
    default: m.DailyTargetsPage,
  })),
);
const PlanVersionDetailPage = lazy(() =>
  import('@/features/planning/components/PlanVersionDetailPage').then((m) => ({
    default: m.PlanVersionDetailPage,
  })),
);
const ShiftReportsPage = lazy(() =>
  import('@/features/operations/components/ShiftReportsPage').then((m) => ({
    default: m.ShiftReportsPage,
  })),
);
const ShiftReportDetailPage = lazy(() =>
  import('@/features/operations/components/ShiftReportDetailPage').then((m) => ({
    default: m.ShiftReportDetailPage,
  })),
);
const DailyReportsPage = lazy(() =>
  import('@/features/operations/components/DailyReportsPage').then((m) => ({
    default: m.DailyReportsPage,
  })),
);
const DailyReportDetailPage = lazy(() =>
  import('@/features/operations/components/DailyReportDetailPage').then((m) => ({
    default: m.DailyReportDetailPage,
  })),
);
const DelayAnalysisPage = lazy(() =>
  import('@/features/operations/components/DelayAnalysisPage').then((m) => ({
    default: m.DelayAnalysisPage,
  })),
);
const VariancePage = lazy(() =>
  import('@/features/operations/components/VariancePage').then((m) => ({
    default: m.VariancePage,
  })),
);
const VarianceThresholdsPage = lazy(() =>
  import('@/features/operations/components/VarianceThresholdsPage').then((m) => ({
    default: m.VarianceThresholdsPage,
  })),
);
const SiteViewPage = lazy(() =>
  import('@/features/operations/components/SiteViewPage').then((m) => ({
    default: m.SiteViewPage,
  })),
);
const ExceptionsPage = lazy(() =>
  import('@/features/operations/components/ExceptionsPage').then((m) => ({
    default: m.ExceptionsPage,
  })),
);
const AlertRulesPage = lazy(() =>
  import('@/features/operations/components/AlertRulesPage').then((m) => ({
    default: m.AlertRulesPage,
  })),
);
const FuelIssuesPage = lazy(() =>
  import('@/features/operations/components/FuelIssuesPage').then((m) => ({
    default: m.FuelIssuesPage,
  })),
);
const FuelTankDipsPage = lazy(() =>
  import('@/features/operations/components/FuelTankDipsPage').then((m) => ({
    default: m.FuelTankDipsPage,
  })),
);
const OrganisationChartPage = lazy(() =>
  import('@/features/leadership-hierarchy/components/OrganisationChartPage').then((m) => ({
    default: m.OrganisationChartPage,
  })),
);
const SanitationRoundPage = lazy(() =>
  import('@/features/daily-activity/components/SanitationRoundPage').then((m) => ({
    default: m.SanitationRoundPage,
  })),
);
const WasteStatusPage = lazy(() =>
  import('@/features/daily-activity/components/WasteStatusPage').then((m) => ({
    default: m.WasteStatusPage,
  })),
);
const ActivityCountersPage = lazy(() =>
  import('@/features/daily-activity/components/ActivityCountersPage').then((m) => ({
    default: m.ActivityCountersPage,
  })),
);
const DailyActivityListsPage = lazy(() =>
  import('@/features/daily-activity/components/DailyActivityListsPage').then((m) => ({
    default: m.DailyActivityListsPage,
  })),
);
const PendingActionsPage = lazy(() =>
  import('@/features/pending-actions/components/PendingActionsPage').then((m) => ({
    default: m.PendingActionsPage,
  })),
);
const PendingActionItemsPage = lazy(() =>
  import('@/features/pending-actions/components/PendingActionItemsPage').then((m) => ({
    default: m.PendingActionItemsPage,
  })),
);
const MyReportingLinePage = lazy(() =>
  import('@/features/leadership-hierarchy/components/MyReportingLinePage').then((m) => ({
    default: m.MyReportingLinePage,
  })),
);
const RoleDetailPage = lazy(() =>
  import('@/features/tenant-config/components/RoleDetailPage').then((m) => ({
    default: m.RoleDetailPage,
  })),
);
const OnboardingChecklistPage = lazy(() =>
  import('@/features/tenant-config/components/OnboardingChecklistPage').then((m) => ({
    default: m.OnboardingChecklistPage,
  })),
);
const FeatureFlagsPage = lazy(() =>
  import('@/features/tenant-config/components/FeatureFlagsPage').then((m) => ({
    default: m.FeatureFlagsPage,
  })),
);
const FeatureFlagsConsolePage = lazy(() =>
  import('@/features/platform-feature-flags/components/FeatureFlagsConsolePage').then(
    (m) => ({ default: m.FeatureFlagsConsolePage }),
  ),
);
const NotificationPreferencesPage = lazy(() =>
  import('@/features/tenant-config/components/NotificationPreferencesPage').then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);
const HierarchyNamingPage = lazy(() =>
  import('@/features/operations/components/HierarchyNamingPage').then((m) => ({
    default: m.HierarchyNamingPage,
  })),
);
const WorkflowConfigPage = lazy(() =>
  import('@/features/tenant-config/components/WorkflowConfigPage').then((m) => ({
    default: m.WorkflowConfigPage,
  })),
);
const CustodyApprovalPolicyPage = lazy(() =>
  import('@/features/tenant-config/components/CustodyApprovalPolicyPage').then(
    (m) => ({ default: m.CustodyApprovalPolicyPage }),
  ),
);
const TenantSettingsPage = lazy(() =>
  import('@/features/tenant-config/components/TenantSettingsPage').then((m) => ({
    default: m.TenantSettingsPage,
  })),
);
const BrandingSettingsPage = lazy(() =>
  import('@/features/tenant-config/components/BrandingSettingsPage').then((m) => ({
    default: m.BrandingSettingsPage,
  })),
);
const CustomDomainPage = lazy(() =>
  import('@/features/tenant-config/components/CustomDomainPage').then((m) => ({
    default: m.CustomDomainPage,
  })),
);
const NotificationPolicyPage = lazy(() =>
  import('@/features/tenant-config/components/NotificationPolicyPage').then((m) => ({
    default: m.NotificationPolicyPage,
  })),
);
const HelpCenterPage = lazy(() =>
  import('@/features/tenant-config/components/HelpCenterPage').then((m) => ({
    default: m.HelpCenterPage,
  })),
);
const MySupportTicketsPage = lazy(() =>
  import('@/features/support/components/MySupportTicketsPage').then((m) => ({
    default: m.MySupportTicketsPage,
  })),
);
const SupportTicketQueuePage = lazy(() =>
  import('@/features/support/components/SupportTicketQueuePage').then((m) => ({
    default: m.SupportTicketQueuePage,
  })),
);
const MyProfilePage = lazy(() =>
  import('@/features/tenant-config/components/MyProfilePage').then((m) => ({
    default: m.MyProfilePage,
  })),
);
const KYBSubmissionPage = lazy(() =>
  import('@/features/tenant-config/components/KYBSubmissionPage').then((m) => ({
    default: m.KYBSubmissionPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/components/errors').then((m) => ({ default: m.NotFoundPage })),
);
const ForbiddenPage = lazy(() =>
  import('@/components/errors').then((m) => ({ default: m.ForbiddenPage })),
);
const BadRequestPage = lazy(() =>
  import('@/components/errors').then((m) => ({ default: m.BadRequestPage })),
);
const ServerErrorPage = lazy(() =>
  import('@/components/errors').then((m) => ({ default: m.ServerErrorPage })),
);
import { RequirePermission } from '@/components/shared/require-permission';
import { RequireRole } from '@/components/shared/require-role';
import { EUserRole } from '@/types';
import { FUEL_VIEW_PERMISSIONS, MINE_SETUP_PERMISSIONS, PERMISSIONS } from '@/lib/permissions';
const TraceabilityDashboardPage = lazy(() =>
  import('@/features/operations/components/TraceabilityDashboardPage').then((m) => ({
    default: m.TraceabilityDashboardPage,
  })),
);

const Gated = ({ flag, children }: { flag: EFeatureFlag; children: ReactNode }) => (
  <FeatureFlag flag={flag} fallback={<FeatureDisabledPlaceholder />}>
    {children}
  </FeatureFlag>
);

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth */}
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<LoginForm />} />
          <Route path="/auth/set-password" element={<SetPasswordForm />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordForm />} />
          <Route path="/auth/reset-password" element={<ResetPasswordForm />} />
        </Route>

        {/* Tenant Admin Portal — ZERO PLACEHOLDERS */}
        <Route element={<DashboardLayout />}>
          <Route path="/admin/dashboard" element={<DashboardPage />} />
          <Route
            path="/admin/mine-setup"
            element={
              <RequirePermission allOf={MINE_SETUP_PERMISSIONS}>
                <MineSetupWizardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inspector-onboarding"
            element={<InspectorOnboardingPage />}
          />

          {/* Super Admin — each route has its own dedicated screen */}
          <Route
            path="/admin/tenants"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <TenantsListPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/tenants/onboarding"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <OnboardingQueuePage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/tenants/provision"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <ProvisionTenantPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/tenants/:id"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <SATenantDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/platform-users/impersonation"
            element={
              <RequirePermission permission={PERMISSIONS.IMPERSONATION_VIEW}>
                <ImpersonationPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <Navigate to="/admin/feature-flags/global" replace />
              </RequireRole>
            }
          />
          <Route
            path="/admin/feature-flags/global"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <FeatureFlagsConsolePage tab="global" />
              </RequireRole>
            }
          />
          <Route
            path="/admin/feature-flags/tenants"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <FeatureFlagsConsolePage tab="tenants" />
              </RequireRole>
            }
          />
          <Route
            path="/admin/feature-flags/users"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <FeatureFlagsConsolePage tab="users" />
              </RequireRole>
            }
          />
          <Route
            path="/admin/product-analytics"
            element={<Navigate to="/admin/analytics/usage" replace />}
          />
          <Route
            path="/admin/system-health"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <SystemHealthPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/system-health/service/:service"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <ServiceHealthDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/system-health/queues"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <JobQueuesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/system-health/errors"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <ErrorLogsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/security/events"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <SecurityFeedPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/security/failed-logins"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <FailedLoginsPage />
              </RequireRole>
            }
          />
          {/* Platform operator surfaces: every one of these reads or writes
              across tenants, and the API gates them on SUPER_ADMIN. Gating the
              route to match means a tenant admin who types the URL is sent back
              to their dashboard rather than onto a page that can only error.
              Tenant-facing twins (/admin/audit-log, /admin/insights/usage) keep
              their permission gates — those are scoped to the caller. */}
          <Route
            path="/admin/platform-audit"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <PlatformAuditPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/analytics/usage"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <UsagePlatformAnalyticsPage />
              </RequireRole>
            }
          />
          {/* Tenant-facing twin. Same permission, different surface: the server
              scopes it to the caller's workspace. */}
          <Route
            path="/admin/insights/usage"
            element={
              <RequirePermission permission={PERMISSIONS.ANALYTICS_VIEW}>
                <TenantUsageAnalyticsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/platform-settings"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <PlatformSettingsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/platform-settings/security"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <SecurityPoliciesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/kyb-config"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <KYBConfigPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/kyb-reviews"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <KYBReviewsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/kyb-reviews/:tenantId"
            element={
              <RequireRole roles={[EUserRole.SUPER_ADMIN]}>
                <KYBReviewDetailPage />
              </RequireRole>
            }
          />

          {/* Compliance */}
          <Route
            path="/admin/compliance/licenses"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <LicenseRegisterPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/licenses/new"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_CREATE}>
                <AddLicensePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/licenses/renewals"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <LicenseRenewalsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/licenses/:id"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <LicenseDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/licenses/:id/edit"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_EDIT}>
                <EditLicensePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/requirements"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <ComplianceRequirementsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/requirements/:id"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <RequirementDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/requirements/:id/edit"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_EDIT}>
                <EditRequirementPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/calendar"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <ComplianceCalendarPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/alerts"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <PermitAlertsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/commitments"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <CommitmentTrackerPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/commitments/new"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_CREATE}>
                <AddCommitmentPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/commitments/:id"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <CommitmentDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/documents"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <DocumentRepositoryPage />
              </RequirePermission>
            }
          />
          {/* <Route path="/admin/compliance/reports" element={<ComplianceReportingPage />} /> */}
          <Route
            path="/admin/compliance/reports"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <ComplianceReportsHubPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/compliance/inspections"
            element={
              <RequirePermission permission={PERMISSIONS.COMPLIANCE_VIEW}>
                <InspectionFeedbackPage />
              </RequirePermission>
            }
          />

          {/* Developer platform. Every route is gated on the DEVELOPER_API flag,
              which ships disabled — a tenant sees this only once it is enabled. */}
          <Route
            path="/admin/developer/api-keys"
            element={
              <RequirePermission permission={PERMISSIONS.APIKEY_VIEW}>
                <Gated flag={Flag.DEVELOPER_API}>
                  <ApiKeysPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/developer/webhooks"
            element={
              <RequirePermission permission={PERMISSIONS.WEBHOOK_VIEW}>
                <Gated flag={Flag.DEVELOPER_API}>
                  <WebhooksPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/developer/deliveries"
            element={
              <RequirePermission permission={PERMISSIONS.WEBHOOK_VIEW}>
                <Gated flag={Flag.DEVELOPER_API}>
                  <DeliveryLogPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/developer/ip-allowlist"
            element={
              <RequirePermission permission={PERMISSIONS.IP_ALLOWLIST_VIEW}>
                <Gated flag={Flag.DEVELOPER_API}>
                  <IpAllowlistPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Geology & Resource Management — Appendix B.4 / §6.2 */}
          <Route
            path="/admin/geology/drill-holes"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <DrillHoleRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/drill-holes/:id"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <DrillHoleDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/blocks"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <ResourceBlocksPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/statements"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <ResourceStatementsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/statements/:id"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <ResourceStatementDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/mapping"
            element={
              <RequirePermission permission={PERMISSIONS.GEOLOGY_VIEW}>
                <Gated flag={Flag.GEOLOGY_MODULE}>
                  <MapRecordsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/geology/grade-samples"
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.GRADE_SAMPLE_VIEW,
                  PERMISSIONS.GEOLOGY_VIEW,
                  PERMISSIONS.LIMS_VIEW,
                  PERMISSIONS.OPERATIONS_VIEW,
                ]}
              >
                <GradeSamplesPage />
              </RequirePermission>
            }
          />

          {/* Laboratory (LIMS) — Appendix B.4 / §6.5. Every route is gated on a
              lims permission: that gate IS the §6.5 confidentiality control. */}
          <Route
            path="/admin/laboratory/samples"
            element={
              <RequirePermission permission={PERMISSIONS.LIMS_VIEW}>
                <Gated flag={Flag.LIMS_MODULE}>
                  <LabSampleRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/laboratory/samples/:id"
            element={
              <RequirePermission permission={PERMISSIONS.LIMS_VIEW}>
                <Gated flag={Flag.LIMS_MODULE}>
                  <LabSampleDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/laboratory/qaqc"
            element={
              <RequirePermission permission={PERMISSIONS.LIMS_VIEW}>
                <Gated flag={Flag.LIMS_MODULE}>
                  <BatchQaqcPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/laboratory/certificates"
            element={
              <RequirePermission permission={PERMISSIONS.LIMS_VIEW}>
                <Gated flag={Flag.LIMS_MODULE}>
                  <GradeCertificatesPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/laboratory/import"
            element={
              <RequirePermission permission={PERMISSIONS.LIMS_CREATE}>
                <Gated flag={Flag.LIMS_MODULE}>
                  <ImportResultsPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Ore Processing — Appendix B.4 / §6.1 */}
          <Route
            path="/admin/processing/plants"
            element={
              <RequirePermission permission={PERMISSIONS.ORE_PROCESSING_VIEW}>
                <Gated flag={Flag.ORE_PROCESSING_MODULE}>
                  <PlantsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/processing/plants/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ORE_PROCESSING_VIEW}>
                <Gated flag={Flag.ORE_PROCESSING_MODULE}>
                  <PlantDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/processing/batches"
            element={
              <RequirePermission permission={PERMISSIONS.ORE_PROCESSING_VIEW}>
                <Gated flag={Flag.ORE_PROCESSING_MODULE}>
                  <ProcessingBatchesPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/processing/batches/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ORE_PROCESSING_VIEW}>
                <Gated flag={Flag.ORE_PROCESSING_MODULE}>
                  <ProcessingBatchDetailPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Surveying & Mapping — Appendix B.4 / §6.6 */}
          <Route
            path="/admin/surveying/surveys"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEYING_VIEW}>
                <Gated flag={Flag.SURVEYING_MODULE}>
                  <SurveyRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/surveying/surveys/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEYING_VIEW}>
                <Gated flag={Flag.SURVEYING_MODULE}>
                  <SurveyDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/surveying/reconciliation"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEYING_VIEW}>
                <Gated flag={Flag.SURVEYING_MODULE}>
                  <ReconciliationQueuePage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/surveying/coordinate-system"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEYING_VIEW}>
                <Gated flag={Flag.SURVEYING_MODULE}>
                  <CoordinateSystemPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Procurement & Vendors — Appendix B.4 / §6.4 */}
          <Route
            path="/admin/procurement/vendors"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_VIEW}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <VendorsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/procurement/vendors/:id"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_VIEW}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <VendorDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/procurement/purchase-orders"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_VIEW}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <PurchaseOrdersPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/procurement/purchase-orders/:id"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_VIEW}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <PurchaseOrderDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/procurement/approval-bands"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_VIEW}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <ApprovalBandsPage />
                </Gated>
              </RequirePermission>
            }
          />
          {/* Spend analytics is the one procurement surface behind `export`:
              the backend gates its endpoint on PROCUREMENT_EXPORT. */}
          <Route
            path="/admin/procurement/spend"
            element={
              <RequirePermission permission={PERMISSIONS.PROCUREMENT_EXPORT}>
                <Gated flag={Flag.PROCUREMENT_MODULE}>
                  <SpendAnalyticsPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Supply Chain & Logistics — Appendix B.4 / §6.7 */}
          <Route
            path="/admin/logistics/carriers"
            element={
              <RequirePermission permission={PERMISSIONS.LOGISTICS_VIEW}>
                <Gated flag={Flag.LOGISTICS_MODULE}>
                  <CarriersPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/logistics/carriers/:id"
            element={
              <RequirePermission permission={PERMISSIONS.LOGISTICS_VIEW}>
                <Gated flag={Flag.LOGISTICS_MODULE}>
                  <CarrierDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/logistics/dispatches"
            element={
              <RequirePermission permission={PERMISSIONS.LOGISTICS_VIEW}>
                <Gated flag={Flag.LOGISTICS_MODULE}>
                  <DispatchBoardPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/logistics/dispatches/:id"
            element={
              <RequirePermission permission={PERMISSIONS.LOGISTICS_VIEW}>
                <Gated flag={Flag.LOGISTICS_MODULE}>
                  <DispatchDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/logistics/discrepancies"
            element={
              <RequirePermission permission={PERMISSIONS.LOGISTICS_VIEW}>
                <Gated flag={Flag.LOGISTICS_MODULE}>
                  <DiscrepancyQueuePage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Operations */}
          <Route
            path="/admin/operations/hierarchy"
            element={
              <RequirePermission permission={PERMISSIONS.OPERATIONS_VIEW}>
                <SiteHierarchyPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/zones/:zoneId"
            element={
              <RequirePermission permission={PERMISSIONS.OPERATIONS_VIEW}>
                <ZoneDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/alerts"
            element={
              <RequirePermission permission={PERMISSIONS.ALERT_VIEW}>
                <CriticalAlertsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/production"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_LOG_VIEW}>
                <ProductionOverviewPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/production/daily"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_LOG_VIEW}>
                <DailyProductionLogPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/shift-logs"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_LOG_VIEW}>
                <ShiftLogsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/shifts"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_VIEW}>
                <Gated flag={Flag.SHIFT_MANAGEMENT}>
                  <ShiftsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/shifts/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_VIEW}>
                <Gated flag={Flag.SHIFT_MANAGEMENT}>
                  <ShiftDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/shift-reports"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_REPORT_VIEW}>
                <Gated flag={Flag.SHIFT_MANAGEMENT}>
                  <ShiftReportsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/shift-reports/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_REPORT_VIEW}>
                <Gated flag={Flag.SHIFT_MANAGEMENT}>
                  <ShiftReportDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/daily-reports"
            element={
              <RequirePermission permission={PERMISSIONS.DAILYREPORT_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <DailyReportsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/daily-reports/:id"
            element={
              <RequirePermission permission={PERMISSIONS.DAILYREPORT_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <DailyReportDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/delay-analysis"
            element={
              <RequirePermission permission={PERMISSIONS.DELAY_EVENT_VIEW}>
                <Gated flag={Flag.SHIFT_MANAGEMENT}>
                  <DelayAnalysisPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/variance"
            element={
              <RequirePermission permission={PERMISSIONS.VARIANCE_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <VariancePage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/variance-thresholds"
            element={
              <RequirePermission permission={PERMISSIONS.VARIANCE_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <VarianceThresholdsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/sites/:siteId/view"
            element={
              <RequirePermission permission={PERMISSIONS.VARIANCE_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <SiteViewPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/exceptions"
            element={
              <RequirePermission permission={PERMISSIONS.ALERT_EVENT_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <ExceptionsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/alert-rules"
            element={
              <RequirePermission permission={PERMISSIONS.ALERT_RULE_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <AlertRulesPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/fuel-issues"
            element={
              <RequirePermission anyOf={FUEL_VIEW_PERMISSIONS}>
                <FuelIssuesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/fuel-tank-dips"
            element={
              <RequirePermission anyOf={FUEL_VIEW_PERMISSIONS}>
                <FuelTankDipsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/development"
            element={
              <RequirePermission permission={PERMISSIONS.DEVELOPMENT_SHIFT_VIEW}>
                <DevelopmentTrackerPage />
              </RequirePermission>
            }
          />
          {/* FR-DEV-005 — Working Day Calendar */}
          <Route
            path="/admin/operations/working-days"
            element={
              <RequirePermission permission={PERMISSIONS.WORKING_DAY_VIEW}>
                <WorkingDayCalendarPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/bags"
            element={
              <RequirePermission permission={PERMISSIONS.MEU_VIEW_LIST}>
                <BagTrackingPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/bags/:id"
            element={
              <RequirePermission anyOf={[PERMISSIONS.MEU_VIEW_STATS, PERMISSIONS.MEU_VIEW_CHAIN]}>
                <MEUDetailPage />
              </RequirePermission>
            }
          />
          {/* FR-BLAST §6.4 — blasting routes gated by the BLASTING_MODULE feature flag */}
          <Route
            path="/admin/operations/blasting"
            element={
              <RequirePermission permission={PERMISSIONS.SHIFT_LOG_VIEW}>
                <Gated flag={Flag.BLASTING_MODULE}>
                  <BlastingLogPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/blaster-certificates"
            element={
              <RequirePermission permission={PERMISSIONS.BLASTER_CERTIFICATE_VIEW}>
                <Gated flag={Flag.BLASTING_MODULE}>
                  <BlasterCertificateRegistryPage />
                </Gated>
              </RequirePermission>
            }
          />
          {/* FR-EQUIP §6.5 — equipment + material logs gated by the STOCK_MODULE feature flag */}
          <Route
            path="/admin/operations/equipment-logs"
            element={
              <RequirePermission permission={PERMISSIONS.EQUIPMENT_LOG_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <EquipmentShiftLogPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/material-availability"
            element={
              <RequirePermission permission={PERMISSIONS.MATERIAL_AVAILABILITY_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <MaterialAvailabilityPage />
                </Gated>
              </RequirePermission>
            }
          />
          {/* FR-OPS §6.6 — Production tracking (revised) */}
          <Route
            path="/admin/operations/production/logs"
            element={
              <RequirePermission permission={PERMISSIONS.PRODUCTION_LOG_VIEW}>
                <ProductionLogsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/production/weekly-summary"
            element={
              <RequirePermission permission={PERMISSIONS.OPERATIONS_REPORT_VIEW}>
                <WeeklyProductionSummaryPage />
              </RequirePermission>
            }
          />
          {/* §6.3 custom KPI builder — same tier as production targets, since a
              custom KPI becomes something the whole tenant is measured against. */}
          <Route
            path="/admin/dashboard/kpis"
            element={
              <RequireRole roles={[EUserRole.MINE_DIRECTOR, EUserRole.MINE_MANAGER]}>
                <RequirePermission permission={PERMISSIONS.KPI_TARGET_VIEW}>
                  <KpiBuilderPage />
                </RequirePermission>
              </RequireRole>
            }
          />
          {/* FR-OPS-014 — production targets are configured by the Mine Manager / Director */}
          <Route
            path="/admin/operations/production/targets"
            element={
              <RequireRole roles={[EUserRole.MINE_DIRECTOR, EUserRole.MINE_MANAGER]}>
                <RequirePermission permission={PERMISSIONS.PRODUCTION_TARGET_VIEW}>
                  <ProductionTargetConfigPage />
                </RequirePermission>
              </RequireRole>
            }
          />
          {/* FR-CRYPTO-030 §6.9.4 — Integrity Verification Report */}
          <Route
            path="/admin/operations/integrity-report"
            element={
              <RequireRole
                roles={[
                  EUserRole.SUPER_ADMIN,
                  EUserRole.IT_ADMINISTRATOR,
                  EUserRole.MINING_CLERK,
                  EUserRole.MINE_DIRECTOR,
                  EUserRole.MINE_MANAGER,
                  EUserRole.GOVERNMENT_INSPECTOR,
                ]}
              >
                <RequirePermission permission={PERMISSIONS.INTEGRITY_REPORT_VIEW}>
                  <IntegrityReportPage />
                </RequirePermission>
              </RequireRole>
            }
          />
          <Route
            path="/admin/operations/traceability"
            element={
              <RequireRole roles={[EUserRole.MINE_DIRECTOR, EUserRole.MINE_MANAGER]}>
                <RequirePermission anyOf={[PERMISSIONS.OPERATIONS_ANALYTICS_VIEW_MEU_TRACEABILITY, PERMISSIONS.OPERATIONS_ANALYTICS_VIEW_CUSTODY_INTEGRITY]}>
                  <TraceabilityDashboardPage />
                </RequirePermission>
              </RequireRole>
            }
          />
          <Route
            path="/admin/workforce/labour-tracking"
            element={
              <RequirePermission permission={PERMISSIONS.TEAM_LABOUR_LOG_VIEW}>
                <TeamLabourLogPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/labour-tracking/:id"
            element={
              <RequirePermission permission={PERMISSIONS.TEAM_LABOUR_LOG_VIEW}>
                <LabourRecordDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/tailings"
            element={
              <RequirePermission anyOf={[PERMISSIONS.TSF_CELL_VIEW, PERMISSIONS.TAILINGS_VIEW, PERMISSIONS.LOT_VIEW]}>
                <TailingsTrackingPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/integrity"
            element={
              <RequireRole
                roles={[
                  EUserRole.SUPER_ADMIN,
                  EUserRole.IT_ADMINISTRATOR,
                  EUserRole.MINING_CLERK,
                  EUserRole.MINE_DIRECTOR,
                  EUserRole.MINE_MANAGER,
                  EUserRole.MINE_ENGINEER,
                  EUserRole.GOVERNMENT_INSPECTOR,
                ]}
              >
                <RequirePermission anyOf={[PERMISSIONS.HASH_CHAIN_VIEW_STATS, PERMISSIONS.HASH_CHAIN_VIEW_BLOCKS, PERMISSIONS.HASH_CHAIN_VIEW_HEAD_DETAIL]}>
                  <HashChainVerificationPage />
                </RequirePermission>
              </RequireRole>
            }
          />
          <Route
            path="/admin/operations/lots"
            element={
              <RequirePermission permission={PERMISSIONS.LOT_VIEW}>
                <LotManagementPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/lots/:id"
            element={
              <RequirePermission permission={PERMISSIONS.LOT_VIEW}>
                <LotDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/certificate/:lotId"
            element={
              <RequirePermission permission={PERMISSIONS.LOT_VIEW}>
                <TraceabilityCertificatePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/reports/weekly"
            element={
              <RequirePermission permission={PERMISSIONS.OPERATIONS_REPORT_VIEW}>
                <WeeklyMineReportPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/hierarchy-naming"
            element={
              <RequirePermission permission={PERMISSIONS.OPERATIONS_VIEW}>
                <HierarchyNamingPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/working-places"
            element={
              <RequirePermission permission={PERMISSIONS.WORKINGPLACE_VIEW}>
                <Gated flag={Flag.WORKING_PLACES_REGISTER}>
                  <WorkingPlacesRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/workplace-types"
            element={
              <RequirePermission permission={PERMISSIONS.WORKINGPLACETYPE_EDIT}>
                <Gated flag={Flag.WORKING_PLACES_REGISTER}>
                  <WorkingPlaceTypesPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/metrics"
            element={
              <RequirePermission permission={PERMISSIONS.PLANNINGMETRICS_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <PlanningMetricsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/code-lists"
            element={
              <RequirePermission permission={PERMISSIONS.PLANNINGCODES_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <PlanningCodeListsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/plans"
            element={
              <RequirePermission permission={PERMISSIONS.PLAN_VERSION_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <PlanVersionsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/daily-targets"
            element={
              <RequirePermission permission={PERMISSIONS.PLAN_DAILY_TARGET_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <DailyTargetsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/calendar"
            element={
              <RequirePermission permission={PERMISSIONS.PLANNINGPROFILE_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <SiteOperatingCalendarPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/operations/planning/plans/:id"
            element={
              <RequirePermission permission={PERMISSIONS.PLAN_VERSION_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <PlanVersionDetailPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Workforce */}
          <Route
            path="/admin/workforce/workers"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_VIEW}>
                <WorkerRegistryPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/worker-approvals"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_VIEW}>
                <WorkerApprovalsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/workers/new"
            element={
              <RequirePermission permission={PERMISSIONS.WORKFORCE_CREATE}>
                <WorkerRegistrationForm />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/workers/:id"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_VIEW}>
                <WorkerProfilePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/attendance"
            element={
              <RequirePermission permission={PERMISSIONS.ATTENDANCE_VIEW}>
                <AttendanceListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/attendance/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ATTENDANCE_VIEW}>
                <AttendanceDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/compensation"
            element={
              <RequirePermission anyOf={[PERMISSIONS.COMPENSATION_RULE_VIEW, PERMISSIONS.PRICING_RULE_READ]}>
                <CompensationConfigPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/workforce/subcontractors"
            element={
              <RequirePermission permission={PERMISSIONS.SUBCONTRACTOR_VIEW}>
                <FeatureFlag
                  flag={Flag.SUBCONTRACTOR_MODULE}
                  fallback={<FeatureDisabledPlaceholder />}
                >
                  <SubcontractorsPage />
                </FeatureFlag>
              </RequirePermission>
            }
          />

          {/* Safety & Environment */}
          <Route
            path="/admin/safety/dashboard"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <SafetyDashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/incidents"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <IncidentRegisterPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/incidents/new"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_CREATE}>
                <ReportIncidentPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/incidents/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <IncidentDetailPage />
              </RequirePermission>
            }
          />

          {/* Visitors Induction Form */}
          <Route
            path="/admin/visitors/new"
            element={
              <RequirePermission permission={PERMISSIONS.VISITOR_CREATE}>
                <Gated flag={Flag.VISITOR_MANAGEMENT_MODULE}>
                  <VisitorInductionFormPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/visitors/register"
            element={
              <RequirePermission permission={PERMISSIONS.VISITOR_VIEW}>
                <Gated flag={Flag.VISITOR_MANAGEMENT_MODULE}>
                  <VisitorRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/visitors/approvals"
            element={
              <RequirePermission permission={PERMISSIONS.VISITOR_APPROVE}>
                <Gated flag={Flag.VISITOR_MANAGEMENT_MODULE}>
                  <VisitorApprovalsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/visitors/:id"
            element={
              <RequirePermission permission={PERMISSIONS.VISITOR_VIEW}>
                <Gated flag={Flag.VISITOR_MANAGEMENT_MODULE}>
                  <VisitorDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/incident-types"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <IncidentTypeConfigPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/inspections"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <InspectionSchedulePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/inspections/new"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_CREATE}>
                <InspectionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/inspections/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <InspectionDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/inspection-types"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <InspectionTypeConfigPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/inspection-checklists"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <InspectionChecklistTemplatePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/risk-thresholds"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <RiskClassificationConfigPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/hazards"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <HazardRegisterPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/equipment"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_EQUIPMENT_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <SafetyEquipmentPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/corrective-actions"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <CorrectiveActionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/corrective-actions/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SAFETY_VIEW}>
                <CorrectiveActionDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/environment"
            element={
              <RequirePermission permission={PERMISSIONS.ENVIRONMENT_VIEW}>
                <FeatureFlag
                  flag={Flag.ENVIRONMENTAL_MODULE}
                  fallback={<FeatureDisabledPlaceholder />}
                >
                  <EnvironmentalMonitoringPage />
                </FeatureFlag>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/environment/incidents"
            element={
              <RequirePermission permission={PERMISSIONS.ENVIRONMENT_VIEW}>
                <Gated flag={Flag.ENVIRONMENTAL_MODULE}>
                  <EnvironmentalIncidentsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/environment/incidents/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ENVIRONMENT_VIEW}>
                <Gated flag={Flag.ENVIRONMENTAL_MODULE}>
                  <EnvironmentalIncidentDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/safety/environment/compliance"
            element={
              <RequirePermission permission={PERMISSIONS.ENVIRONMENT_VIEW}>
                <Gated flag={Flag.ENVIRONMENTAL_MODULE}>
                  <EnvironmentalCompliancePage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Inventory */}
          <Route
            path="/admin/inventory/materials"
            element={
              <RequirePermission anyOf={[PERMISSIONS.INVENTORY_MATERIAL_VIEW, PERMISSIONS.INVENTORY_EQUIPMENT_VIEW]}>
                <FeatureFlag
                  flag={Flag.STOCK_MODULE}
                  fallback={<FeatureDisabledPlaceholder />}
                >
                  <InventoryPage />
                </FeatureFlag>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/materials/:materialId"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_MATERIAL_VIEW}>
                <FeatureFlag
                  flag={Flag.STOCK_MODULE}
                  fallback={<FeatureDisabledPlaceholder />}
                >
                  <MaterialDetailPage />
                </FeatureFlag>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/stock-on-hand"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_STOCK_ON_HAND_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <StockOnHandPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/movements"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_STOCK_MOVEMENT_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <StockMovementsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/counts"
            element={
              <RequirePermission permission={PERMISSIONS.STOCK_COUNT_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <StockCountsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/counts/:countId"
            element={
              <RequirePermission permission={PERMISSIONS.STOCK_COUNT_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <StockCountDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/stockpile"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_ORE_STOCKPILE_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <OreStockpilePage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/requisitions"
            element={
              <RequirePermission permission={PERMISSIONS.MATERIAL_REQUISITION_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <MaterialRequisitionsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/reconciliation"
            element={
              <RequirePermission permission={PERMISSIONS.MATERIAL_RECONCILIATION_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <MaterialReconciliationPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/categories"
            element={
              <RequirePermission anyOf={[PERMISSIONS.INVENTORY_MATERIAL_CATEGORY_VIEW, PERMISSIONS.INVENTORY_EQUIPMENT_TYPE_VIEW]}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <CategoriesPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/inventory/equipment/availability"
            element={
              <RequirePermission permission={PERMISSIONS.INVENTORY_EQUIPMENT_VIEW}>
                <Gated flag={Flag.STOCK_MODULE}>
                  <EquipmentAvailabilityPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Finance */}
          <Route
            path="/admin/finance/batches"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_BATCH_VIEW}>
                <PaymentBatchesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/batches/:id"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_BATCH_VIEW}>
                <PaymentBatchDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/reconciliation"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_RECONCILIATION_VIEW}>
                <ReconciliationDashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/history"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_HISTORY_VIEW}>
                <PaymentHistoryPage />
              </RequirePermission>
            }
          />
          {/* Payment Configuration is switched off for every user.
          <Route
            path="/admin/finance/config"
            element={
              <RequirePermission permission={PERMISSIONS.FINANCE_VIEW}>
                <PaymentConfigPage />
              </RequirePermission>
            }
          />
          */}
          <Route
            path="/admin/finance/requisitions"
            element={
              <RequirePermission permission={PERMISSIONS.FINANCIAL_REQUISITION_VIEW}>
                <Gated flag={Flag.FINANCIAL_REQUISITIONS}>
                  <FinancialRequisitionRegisterPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/requisitions/new"
            element={
              <RequirePermission permission={PERMISSIONS.FINANCIAL_REQUISITION_CREATE}>
                <Gated flag={Flag.FINANCIAL_REQUISITIONS}>
                  <FinancialRequisitionFormPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/requisitions/:id"
            element={
              <RequirePermission permission={PERMISSIONS.FINANCIAL_REQUISITION_VIEW}>
                <Gated flag={Flag.FINANCIAL_REQUISITIONS}>
                  <FinancialRequisitionDetailPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/requisitions/:id/edit"
            element={
              <RequirePermission permission={PERMISSIONS.FINANCIAL_REQUISITION_EDIT}>
                <Gated flag={Flag.FINANCIAL_REQUISITIONS}>
                  <FinancialRequisitionFormPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/payment-runs"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_RUN_VIEW}>
                <Gated flag={Flag.PAYMENT_RUNS}>
                  <PaymentRunsPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/finance/payment-runs/:id"
            element={
              <RequirePermission permission={PERMISSIONS.PAYMENT_RUN_VIEW}>
                <Gated flag={Flag.PAYMENT_RUNS}>
                  <PaymentRunDetailPage />
                </Gated>
              </RequirePermission>
            }
          />

          {/* Reports — unified tabbed workspace. Each tab is deep-linkable at
            /admin/reports/<tab>; the bare path defaults to the first tab.
            Data Export keeps its own static route (declared before the dynamic
            :tab route, which it outranks). */}
          <Route
            path="/admin/reports"
            element={
              <RequirePermission permission={PERMISSIONS.REPORTS_VIEW}>
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/reports/:tab"
            element={
              <RequirePermission permission={PERMISSIONS.REPORTS_VIEW}>
                <ReportsPage />
              </RequirePermission>
            }
          />

          {/* Users */}
          <Route
            path="/admin/users/list"
            element={
              <RequirePermission permission={PERMISSIONS.USER_READ}>
                <UserManagementPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <RequirePermission permission={PERMISSIONS.USER_READ}>
                <UserDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users/roles"
            element={
              <RequirePermission permission={PERMISSIONS.ROLE_READ}>
                <RolesPermissionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users/roles/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ROLE_READ}>
                <RoleDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users/departments"
            element={
              <RequirePermission permission={PERMISSIONS.DEPARTMENT_READ}>
                <DepartmentsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/organisation"
            element={
              <RequirePermission permission={PERMISSIONS.LEADERSHIP_HIERARCHY_READ}>
                <OrganisationChartPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/organisation/my-reporting-line"
            element={
              <RequirePermission permission={PERMISSIONS.MY_REPORTING_LINE_VIEW}>
                <MyReportingLinePage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/daily-activity/sanitation-round"
            element={
              <Gated flag={Flag.DAILY_ACTIVITY_REPORTS}>
                <RequirePermission permission={PERMISSIONS.DAILYACTIVITY_VIEW}>
                  <SanitationRoundPage />
                </RequirePermission>
              </Gated>
            }
          />
          <Route
            path="/admin/daily-activity/waste-status"
            element={
              <Gated flag={Flag.DAILY_ACTIVITY_REPORTS}>
                <RequirePermission permission={PERMISSIONS.DAILYACTIVITY_VIEW}>
                  <WasteStatusPage />
                </RequirePermission>
              </Gated>
            }
          />
          <Route
            path="/admin/daily-activity/activity-counters"
            element={
              <Gated flag={Flag.DAILY_ACTIVITY_REPORTS}>
                <RequirePermission permission={PERMISSIONS.DAILYACTIVITY_VIEW}>
                  <ActivityCountersPage />
                </RequirePermission>
              </Gated>
            }
          />
          <Route
            path="/admin/daily-activity/lists"
            element={
              <Gated flag={Flag.DAILY_ACTIVITY_REPORTS}>
                <RequirePermission permission={PERMISSIONS.DAILY_ACTIVITY_CODE_LIST_VIEW}>
                  <DailyActivityListsPage />
                </RequirePermission>
              </Gated>
            }
          />
          <Route
            path="/admin/pending-actions"
            element={
              <Gated flag={Flag.PENDING_ACTIONS_VIEW}>
                <RequirePermission permission={PERMISSIONS.PENDING_ACTIONS_VIEW}>
                  <PendingActionsPage />
                </RequirePermission>
              </Gated>
            }
          />
          <Route
            path="/admin/pending-actions/items"
            element={
              <Gated flag={Flag.PENDING_ACTIONS_VIEW}>
                <RequirePermission permission={PERMISSIONS.PENDING_ACTIONS_VIEW}>
                  <PendingActionItemsPage />
                </RequirePermission>
              </Gated>
            }
          />

          {/* Config */}
          <Route
            path="/admin/config/onboarding"
            element={
              <RequirePermission permission={PERMISSIONS.CONFIG_VIEW}>
                <OnboardingChecklistPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/config/features"
            element={
              <RequirePermission permission={PERMISSIONS.CONFIG_VIEW}>
                <FeatureFlagsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/config/financial-year"
            element={
              <RequirePermission permission={PERMISSIONS.CONFIG_VIEW}>
                <Gated flag={Flag.MINE_PLANNING}>
                  <FinancialYearPage />
                </Gated>
              </RequirePermission>
            }
          />
          <Route
            path="/admin/config/notifications"
            element={
              <RequirePermission permission={PERMISSIONS.NOTIFICATION_PREFERENCE_VIEW}>
                <NotificationPreferencesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/config/hierarchy"
            element={<Navigate to="/admin/operations/hierarchy-naming" replace />}
          />
          <Route
            path="/admin/config/workflows"
            element={
              <RequirePermission permission={PERMISSIONS.CONFIG_VIEW}>
                <WorkflowConfigPage />
              </RequirePermission>
            }
          />
          {/* FR-TRACE-014 — custody approval policy (IT Administrator). */}
          <Route
            path="/admin/config/custody-approval"
            element={
              <RequirePermission permission={PERMISSIONS.CUSTODY_APPROVAL_CONFIGURE}>
                <CustodyApprovalPolicyPage />
              </RequirePermission>
            }
          />

          {/* KYB */}
          <Route
            path="/admin/kyb"
            element={
              <RequirePermission permission={PERMISSIONS.TENANT_VIEW}>
                <KYBSubmissionPage />
              </RequirePermission>
            }
          />

          {/* Audit */}
          <Route
            path="/admin/audit-log"
            element={
              <RequirePermission permission={PERMISSIONS.AUDIT_VIEW}>
                <AuditLogPage />
              </RequirePermission>
            }
          />

          {/* Settings */}
          <Route
            path="/admin/settings/profile"
            element={
              <RequirePermission permission={PERMISSIONS.TENANT_VIEW}>
                <TenantSettingsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/settings/branding"
            element={
              <RequirePermission permission={PERMISSIONS.SETTINGS_WRITE}>
                <BrandingSettingsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/settings/custom-domain"
            element={
              <RequirePermission permission={PERMISSIONS.CONFIG_CREATE}>
                <CustomDomainPage />
              </RequirePermission>
            }
          />
          {/* FR2 item 20 — org-wide notification channel ceiling/floor. */}
          <Route
            path="/admin/settings/notification-policy"
            element={
              <RequirePermission permission={PERMISSIONS.SETTINGS_WRITE}>
                <NotificationPolicyPage />
              </RequirePermission>
            }
          />
          <Route path="/admin/settings/my-profile" element={<MyProfilePage />} />

          {/* Help Center — every authenticated tenant user, per backend PreAuthorize(ADMIN, USER). */}
          <Route path="/help-center" element={<HelpCenterPage />} />

          {/* FR14 — Support Tickets */}
          <Route
            path="/admin/support/my-tickets"
            element={
              <RequirePermission permission={PERMISSIONS.MY_SUPPORT_TICKET_VIEW}>
                <MySupportTicketsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/support/queue"
            element={
              <RequirePermission permission={PERMISSIONS.SUPPORT_TICKET_VIEW}>
                <SupportTicketQueuePage />
              </RequirePermission>
            }
          />

          <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        <Route element={<DashboardLayout />}>
          {/* Explicit error routes so the axios interceptor (and any caller)
            can navigate the user here. Each renders the matching themed
            error page with a "Go back" button. */}
          <Route path="/400" element={<BadRequestPage />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          {/* Catch-all 404 — anything not matched above lands here. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}